import React from 'react';
import { MessageSquare, Search, Star, X, Plus } from 'lucide-react';
import { CHAT_GROUPS, CHAT_SUB_TABS, type ChatGroup, type ChatSubFilter } from './chatSegmentConfig';
import { useAuthStore } from '../../store/authStore';

export interface ChatToolbarProps {
  searchText: string;
  onSearchChange: (text: string) => void;
  searchPlaceholder?: string;
  showFilters?: boolean;
  // Segmentos
  activeGroup?: ChatGroup | 'todos';
  onGroupChange?: (group: ChatGroup | 'todos') => void;
  activeSubFilter?: ChatSubFilter;
  onSubFilterChange?: (sub: ChatSubFilter) => void;
  // Filtros
  onlyUnread?: boolean;
  onOnlyUnreadChange?: () => void;
  onlyFavorites?: boolean;
  onOnlyFavoritesChange?: () => void;
  // Catálogo contatos
  onOpenContacts?: () => void;
}

export function ChatToolbar({
  searchText,
  onSearchChange,
  searchPlaceholder = 'Buscar conversas...',
  showFilters = false,
  activeGroup = 'todos',
  onGroupChange,
  activeSubFilter = 'todos',
  onSubFilterChange,
  onlyUnread = false,
  onOnlyUnreadChange,
  onlyFavorites = false,
  onOnlyFavoritesChange,
  onOpenContacts,
}: ChatToolbarProps) {
  const user = useAuthStore((s) => s.user);
  const perfil = user?.perfil || '';

  const visibleOptions = React.useMemo(() => {
    const p = (perfil || '').toUpperCase();

    if (p === 'MUNICIPAL') {
      return [
        { value: 'admin', label: 'Suporte (Administradores)' },
        { value: 'clientes-estado', label: 'Gestão Estadual / SEDUC' },
        { value: 'clientes-municipio', label: 'Minha Equipe (SME)' },
        { value: 'clientes-escola', label: 'Diretores e Escolas Municipais' },
        { value: 'operadores-todos', label: 'Professores e Equipes' },
        { value: 'operadores-professores', label: '└ Professores da Rede' },
        { value: 'operadores-secretaria', label: '└ Secretaria Escolar' },
        { value: 'operadores-coordenadores', label: '└ Coordenação Pedagógica' },
        { value: 'familia-todos', label: 'Família / Responsáveis' },
      ];
    }

    if (p === 'ESCOLA') {
      return [
        { value: 'admin', label: 'Suporte (Administradores)' },
        { value: 'clientes-municipio', label: 'Secretaria Municipal (SME)' },
        { value: 'clientes-estado', label: 'Gestão Estadual / SEDUC' },
        { value: 'clientes-escola', label: 'Equipe da Escola (Diretoria)' },
        { value: 'operadores-todos', label: 'Professores e Equipes' },
        { value: 'operadores-professores', label: '└ Professores da Escola' },
        { value: 'operadores-secretaria', label: '└ Secretaria' },
        { value: 'operadores-coordenadores', label: '└ Coordenação' },
        { value: 'familia-todos', label: 'Família / Responsáveis' },
      ];
    }

    if (p === 'PROFESSOR') {
      return [
        { value: 'admin', label: 'Suporte (Administradores)' },
        { value: 'clientes-escola', label: 'Direção e Secretaria da Escola' },
        { value: 'operadores-professores', label: 'Colegas Professores' },
        { value: 'familia-todos', label: 'Responsáveis das Minhas Turmas' },
      ];
    }

    if (p === 'ESTADUAL') {
      return [
        { value: 'admin', label: 'Suporte (Administradores)' },
        { value: 'clientes-rede', label: 'Gestão de Redes' },
        { value: 'clientes-estado', label: 'Equipe Estadual (SEDUC)' },
        { value: 'clientes-municipio', label: 'Secretarias Municipais (SME)' },
        { value: 'clientes-escola', label: 'Diretores e Escolas' },
        { value: 'operadores-todos', label: 'Professores e Equipes' },
        { value: 'operadores-professores', label: '└ Professores' },
        { value: 'familia-todos', label: 'Família / Responsáveis' },
      ];
    }

    if (p === 'REDE') {
      return [
        { value: 'admin', label: 'Suporte (Administradores)' },
        { value: 'clientes-rede', label: 'Equipe da Rede' },
        { value: 'clientes-estado', label: 'Secretarias Estaduais' },
        { value: 'clientes-municipio', label: 'Secretarias Municipais' },
        { value: 'clientes-escola', label: 'Diretores e Escolas' },
        { value: 'operadores-todos', label: 'Professores da Rede' },
        { value: 'familia-todos', label: 'Família / Responsáveis' },
      ];
    }

    if (p === 'RESPONSAVEL' || p === 'RESPONSÁVEL') {
      return [
        { value: 'admin', label: 'Suporte do Aplicativo' },
        { value: 'clientes-escola', label: 'Escola dos Meus Filhos' },
        { value: 'operadores-professores', label: 'Professores dos Meus Filhos' },
      ];
    }

    return [
      { value: 'admin', label: 'Administradores' },
      { value: 'operadores-todos', label: 'Equipe e Docentes' },
      { value: 'operadores-professores', label: '└ Professores' },
      { value: 'operadores-secretaria', label: '└ Secretaria' },
      { value: 'operadores-coordenadores', label: '└ Coordenação' },
      { value: 'familia-todos', label: 'Família / Responsáveis' },
      { value: 'clientes-todos', label: 'Gestores Públicos (Todos)' },
      { value: 'clientes-escola', label: '└ Diretores e Escolas' },
      { value: 'clientes-municipio', label: '└ Gestão Municipal' },
      { value: 'clientes-estado', label: '└ Gestão Estadual' },
      { value: 'clientes-rede', label: '└ Gestão de Redes' },
    ];
  }, [perfil]);

  const getUnifiedValue = (): string => {
    if (activeGroup === 'todos') return 'todos';
    if (activeGroup === 'admin') return 'admin';
    if (activeGroup === 'operadores') {
      if (activeSubFilter === 'todos') return 'operadores-todos';
      return `operadores-${activeSubFilter}`;
    }
    if (activeGroup === 'familia') {
      if (activeSubFilter === 'todos') return 'familia-todos';
      return `familia-${activeSubFilter}`;
    }
    if (activeGroup === 'clientes') {
      if (activeSubFilter === 'todos') return 'clientes-todos';
      return `clientes-${activeSubFilter}`;
    }
    return 'todos';
  };

  const handleUnifiedChange = (val: string) => {
    if (val === 'todos') {
      onGroupChange?.('todos');
      onSubFilterChange?.('todos');
    } else if (val === 'admin') {
      onGroupChange?.('admin');
      onSubFilterChange?.('todos');
    } else if (val.startsWith('operadores-')) {
      const sub = val.substring('operadores-'.length) as ChatSubFilter;
      onGroupChange?.('operadores');
      onSubFilterChange?.(sub);
    } else if (val.startsWith('familia-')) {
      const sub = val.substring('familia-'.length) as ChatSubFilter;
      onGroupChange?.('familia');
      onSubFilterChange?.(sub);
    } else if (val.startsWith('clientes-')) {
      const sub = val.substring('clientes-'.length) as ChatSubFilter;
      onGroupChange?.('clientes');
      onSubFilterChange?.(sub);
    }
  };

  return (
    <div
      className="ac-chat-toolbar"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '8px 12px 10px 12px',
        borderBottom: '1px solid var(--ac-chat-border)',
        background: 'var(--ac-chat-card-solid)',
        transition: 'background 150ms ease',
      }}
    >
      {/* Linha 1: Input de Busca Estilo WhatsApp */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        <div className="ac-chat-search-wrap" style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--ac-chat-muted)',
              pointerEvents: 'none',
            }}
          />
          <input
            type="text"
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="ac-chat-search"
            style={{
              width: '100%',
              height: 36,
              paddingLeft: 32,
              paddingRight: searchText.trim().length > 0 ? 28 : 8,
              borderRadius: 0,
              background: 'transparent',
              border: 'none',
              color: 'var(--ac-chat-text)',
              fontSize: '0.80rem',
              fontWeight: 500,
              outline: 'none',
              boxShadow: 'none',
            }}
          />
          {searchText.trim().length > 0 && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              title="Limpar busca"
              style={{
                position: 'absolute',
                right: 6,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 20,
                height: 20,
                border: 0,
                borderRadius: 99,
                background: 'var(--ac-chat-border)',
                color: 'var(--ac-chat-text)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <X size={12} />
            </button>
          )}
        </div>

        {/* Botão Novo Chat no topo se disponível */}
        {onOpenContacts && (
          <button
            type="button"
            onClick={onOpenContacts}
            title="Novo chat (Catálogo de Contatos)"
            className="ac-chat-plus-btn"
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              border: '1px solid transparent',
              background: 'transparent',
              color: 'var(--ac-chat-text)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'transform 140ms ease, background 140ms ease, border-color 140ms ease, box-shadow 140ms ease, color 140ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--ac-chat-soft)';
              e.currentTarget.style.borderColor = 'var(--ac-chat-border)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.12)';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.color = '#3b82f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'transparent';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.color = 'var(--ac-chat-text)';
            }}
          >
            <Plus size={18} />
          </button>
        )}
      </div>

      {/* Linha 2: Chips de Filtro Rápido WhatsApp */}
      {showFilters && (
        <div
          className="ac-chat-chip-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            paddingBottom: 2,
          }}
        >
          {/* Chip Todos */}
          <button
            type="button"
            onClick={() => {
              if (onlyUnread) onOnlyUnreadChange?.();
              if (onlyFavorites) onOnlyFavoritesChange?.();
              handleUnifiedChange('todos');
            }}
            style={{
              padding: '4px 10px',
              borderRadius: 14,
              fontSize: '0.70rem',
              fontWeight: 700,
              border: !onlyUnread && !onlyFavorites && getUnifiedValue() === 'todos' ? '1px solid #2563eb' : '1px solid var(--ac-chat-border)',
              background: !onlyUnread && !onlyFavorites && getUnifiedValue() === 'todos' ? 'rgba(37, 99, 235, 0.12)' : 'var(--ac-chat-soft)',
              color: !onlyUnread && !onlyFavorites && getUnifiedValue() === 'todos' ? '#2563eb' : 'var(--ac-chat-muted)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 120ms ease',
            }}
          >
            Todas
          </button>

          {/* Chip Não Lidas */}
          <button
            type="button"
            onClick={onOnlyUnreadChange}
            style={{
              padding: '4px 10px',
              borderRadius: 14,
              fontSize: '0.70rem',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              border: onlyUnread ? '1px solid #16a34a' : '1px solid var(--ac-chat-border)',
              background: onlyUnread ? 'rgba(22, 163, 74, 0.14)' : 'var(--ac-chat-soft)',
              color: onlyUnread ? '#16a34a' : 'var(--ac-chat-muted)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 120ms ease',
            }}
          >
            <MessageSquare size={12} />
            <span>Não lidas</span>
          </button>

          {/* Chip Favoritas */}
          <button
            type="button"
            onClick={onOnlyFavoritesChange}
            style={{
              padding: '4px 10px',
              borderRadius: 14,
              fontSize: '0.70rem',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              border: onlyFavorites ? '1px solid #eab308' : '1px solid var(--ac-chat-border)',
              background: onlyFavorites ? 'rgba(234, 179, 8, 0.15)' : 'var(--ac-chat-soft)',
              color: onlyFavorites ? '#ca8a04' : 'var(--ac-chat-muted)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 120ms ease',
            }}
          >
            <Star size={12} style={{ fill: onlyFavorites ? '#ca8a04' : 'none' }} />
            <span>Favoritas</span>
          </button>

          {/* Dropdown de Segmentação em formato de Chip */}
          <select
            value={getUnifiedValue()}
            onChange={(e) => handleUnifiedChange(e.target.value)}
            style={{
              height: 26,
              padding: '0 8px',
              borderRadius: 14,
              border: getUnifiedValue() !== 'todos' ? '1px solid #2563eb' : '1px solid var(--ac-chat-border)',
              background: getUnifiedValue() !== 'todos' ? 'rgba(37, 99, 235, 0.12)' : 'var(--ac-chat-soft)',
              color: getUnifiedValue() !== 'todos' ? '#2563eb' : 'var(--ac-chat-muted)',
              fontSize: '0.70rem',
              fontWeight: 700,
              cursor: 'pointer',
              outline: 'none',
              maxWidth: '11rem',
            }}
          >
            <option value="todos">Segmento: Todos</option>
            {visibleOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
